import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSettings, inr } from "@/lib/api";

const buildSections = (s) => [
  {
    id: "shipping",
    title: "Shipping Policy",
    body: [
      "We ship across India. Orders are dispatched from our facility, typically within 1–2 business days of confirmation.",
      `Shipping is free on orders of ${inr(s.free_shipping_above)} and above. Orders below ${inr(s.free_shipping_above)} carry a flat ${inr(s.shipping_fee)} shipping charge, shown clearly at checkout before you pay.`,
      "Every order is confirmed by our team on a phone call before dispatch. You can check your order status anytime on the Track Order page using your order number and phone number.",
      "Delivery timelines vary by PIN code — most metro cities receive orders in 3–5 business days; remote PIN codes may take a little longer.",
    ],
  },
  {
    id: "returns",
    title: "Return & Refund Policy",
    body: [
      "If your order arrives damaged, has a manufacturing defect, or contains the wrong items, we will replace it or refund you in full.",
      `Please write to us within 7 days of delivery at ${s.contact_email} or call ${s.contact_phone} with your order number and a photo of the issue.`,
      "Approved refunds are processed back to your original payment method (for online payments) or via UPI/bank transfer (for COD orders) within 5–7 business days.",
      "Because notebooks and paper products are made to order in sets and packs, opened or used products cannot be returned unless defective.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    body: [
      "We collect only the information needed to fulfil your order — your name, phone number, email and delivery address — plus any messages you send us.",
      "Your details are used solely for order processing, delivery, order-related communication and, if you subscribe, occasional product updates. We never sell or rent your data.",
      "Online payments are processed securely by Razorpay; we never see or store your card or UPI details.",
      `You may ask us to view or delete your personal data anytime by writing to ${s.contact_email}.`,
    ],
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    body: [
      `Classina® is the house brand of SBK Manufacturing Company, ${s.contact_address.replace(/\n/g, ", ")}.`,
      "All prices are listed in Indian Rupees (₹) and are inclusive of applicable taxes unless stated otherwise. Prices, pack configurations and offers may change without notice; the price shown at checkout at the time of your order applies.",
      "Some products are sold in minimum sets (for example, sets of 6). The minimum quantity is always shown on the product before you add it to your cart.",
      "Orders are confirmed by a phone call from our team before dispatch. Cash on Delivery and pay-on-confirmation orders may be verified before shipping.",
      "Bulk, institutional and customised manufacturing orders are governed by the quotation and terms shared at the time of the order.",
    ],
  },
  {
    id: "faq",
    title: "Frequently Asked Questions",
    body: [
      "What is a 'set'? — Some notebooks are sold in minimum sets (e.g. 6 pieces). You can mix rulings or colours within the set; the cart shows your progress as you add.",
      "What are multi-packs? — Pack options like 6/12/24 pc packs bundle a product at a discounted per-piece price. The bigger the pack, the more you save.",
      "How do I pay? — Cash on Delivery / pay-on-confirmation is available on all orders. Online payment via UPI, cards and netbanking (Razorpay) is being enabled shortly.",
      "How do I track my order? — Use the Track Order page with your order number (starts with CLS-) and the phone number you ordered with.",
      "Do you take bulk or school orders? — Yes. We are a manufacturer, so bulk and institutional pricing is our strength. Use the Trade section or contact form for a quotation.",
      "Can I customise notebooks with our school/company cover? — Yes, custom covers, foiling and ruling are available on bulk orders. Write to us with your requirement.",
    ],
  },
];

const FALLBACK = {
  free_shipping_above: 499,
  shipping_fee: 49,
  contact_phone: "+91 99607 49074",
  contact_email: "info@sbkmanufacturing.com",
  contact_address: "230/4 Nana Peth, Pragati Villa, Pune 411002, Maharashtra, India",
};

export default function PoliciesPage() {
  const [settings, setSettings] = useState(FALLBACK);

  useEffect(() => {
    getSettings()
      .then(setSettings)
      .catch(() => {});
  }, []);

  const sections = buildSections(settings);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-[#F5F4F0]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="policies-logo" className="flex items-center">
            <img src="/classina-logo.png" alt="Classina Products" className="h-8 w-auto" />
          </Link>
          <Link to="/" data-testid="policies-back-link" className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
            ← Back to store
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="policies-page">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">The fine print, kept simple</p>
        <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">
          Policies & FAQ
        </h1>
        <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
          Everything about ordering, shipping, returns and your data — in plain language.
        </p>

        <nav className="mt-8 flex flex-wrap gap-2" data-testid="policies-nav">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`policies-nav-${s.id}`}
              className="rounded-full bg-secondary/60 px-4 py-2 text-xs font-bold text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              {s.title}
            </a>
          ))}
        </nav>

        <div className="mt-12 space-y-14">
          {sections.map((s) => (
            <section key={s.id} id={s.id} data-testid={`policy-${s.id}`} className="scroll-mt-24">
              <h2 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">{s.title}</h2>
              <div className="mt-4 space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm text-muted-foreground leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 rounded-2xl bg-secondary/40 border border-border/60 p-8 text-center">
          <p className="font-serif text-xl font-semibold">Still have a question?</p>
          <p className="text-sm text-muted-foreground mt-2">
            Call <a data-testid="policies-phone" href={`tel:${settings.contact_phone.replace(/[^+\d]/g, "")}`} className="text-primary font-semibold">{settings.contact_phone}</a> or write to{" "}
            <a data-testid="policies-email" href={`mailto:${settings.contact_email}`} className="text-primary font-semibold">{settings.contact_email}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
